import React from 'react';
import { Chip, IconButton, Tooltip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { getCaseInsensitiveValue } from './appsheetFieldUtils';

const normalizeKey = (value) => String(value ?? '').trim().toLowerCase();
const compactKey = (value) => normalizeKey(value).replace(/[\s_]+/g, '');
const normalizeValue = (value) => String(value ?? '').trim().toLowerCase();

const normalizeFieldType = (value) => {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return raw || null;
};

const toFieldMeta = (field) => {
  if (!field) return null;
  if (typeof field === 'string') {
    return { name: field, dataType: null, displayName: null, enumValues: null };
  }
  const name = field?.name || field?.field || field?.id;
  if (!name) return null;
  return {
    name,
    dataType: normalizeFieldType(field?.dataType),
    displayName: field?.displayName ?? null,
    enumValues: Array.isArray(field?.enumValues) ? field.enumValues : field?.enumValues ?? null,
  };
};

const multiSelectFilterFn = (row, id, filterValue) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
  const value = row.getValue(id);
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '')).some((item) => filterValue.includes(item));
  }
  if (typeof value === 'string' && value.includes(',')) {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .some((item) => filterValue.includes(item));
  }
  return filterValue.includes(String(value ?? ''));
};

const buildMultiSelectFilter = (enumValues) => {
  const options = Array.isArray(enumValues)
    ? enumValues.map((value) => String(value)).filter(Boolean)
    : [];
  const base = {
    filterVariant: 'multi-select',
    filterFn: multiSelectFilterFn,
  };
  if (!options.length) return base;
  return { ...base, filterSelectOptions: options };
};

const CHIP_VALUE_COLORS = new Map([
  ['active', 'success'],
  ['live', 'success'],
  ['enabled', 'success'],
  ['yes', 'success'],
  ['true', 'success'],
  ['1', 'success'],
  ['enrolled', 'success'],
  ['in production', 'success'],
  ['pending', 'warning'],
  ['pending live', 'warning'],
  ['in progress', 'warning'],
  ['queued', 'warning'],
  ['inactive', 'error'],
  ['cancelled', 'error'],
  ['canceled', 'error'],
  ['terminated', 'error'],
  ['no', 'error'],
  ['false', 'error'],
  ['0', 'error'],
  ['not enrolled', 'error'],
]);

const CHIP_EXCLUDE_FIELDS = new Set([
  'quantity',
  'itemquantity',
  'dealerprice',
  'filler',
  'baccount',
]);

const CHIP_INCLUDE_FIELDS = new Set([
  'sbqqbillingfrencencyc',
  'sbqqbillingfrequencyc',
  'sbqqcontractactionc',
  'invoicegroupc',
  'occurance',
  'occurrence',
  'productprogram',
  'categorycd',
  'opportunitystage',
  'accountposition',
  'accountpositionc',
  'hasenrollment',
]);

const RETIRED_ORDER_ITEM_FIELDS = new Set([
  'accountisprimary',
  'issecondary',
  'ispopcornelligable',
  'iscadillac',
  'vmtcallstatus',
  'vmtcallerrormessage',
  'rowoverwritten?',
]);

const FIELD_COLOR_RULES = [
  {
    fields: new Set(['hasenrollment']),
    colors: new Map([
      ['true', 'success'],
      ['false', 'error'],
      ['yes', 'success'],
      ['no', 'error'],
      ['1', 'success'],
      ['0', 'error'],
    ]),
  },
];

const isStatusField = (field) => {
  const key = normalizeKey(field);
  if (!key) return false;
  return (
    key.includes('status') ||
    key.includes('enrollment') ||
    key.includes('production') ||
    key.includes('active') ||
    key.includes('enabled')
  );
};

const shouldRenderChip = (field) => {
  const compact = compactKey(field);
  if (!compact) return false;
  if (CHIP_EXCLUDE_FIELDS.has(compact)) return false;
  if (CHIP_INCLUDE_FIELDS.has(compact)) return true;
  return isStatusField(field);
};

const resolveChipColor = (field, value) => {
  const compact = compactKey(field);
  const normalized = normalizeValue(value);
  for (const rule of FIELD_COLOR_RULES) {
    if (rule.fields.has(compact)) {
      return rule.colors.get(normalized) || 'default';
    }
  }
  return CHIP_VALUE_COLORS.get(normalized) || 'default';
};

const formatChipLabel = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const normalized = normalizeValue(value);
  if (normalized === 'true' || normalized === '1') return 'Yes';
  if (normalized === 'false' || normalized === '0') return 'No';
  return String(value).trim();
};

export const buildOrderItemsColumns = ({
  fields,
  resolveAccountName,
  onSendToStaging,
  onOpenMenu,
  itemLabel = 'Order item',
}) => {
  const lowerItemLabel = String(itemLabel || 'Order item').toLowerCase();
  const visibleFields = (fields || [])
    .map(toFieldMeta)
    .filter(Boolean)
    .filter((field) => normalizeKey(field.name) !== 'account_id__c')
    .filter((field) => !RETIRED_ORDER_ITEM_FIELDS.has(compactKey(field.name)));
  const sendColumn = {
    id: 'sendToStaging',
    header: 'Stage',
    size: 72,
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    Cell: ({ row }) => (
      <Tooltip title={`Send ${lowerItemLabel} to staging; touch or right-click for options`}>
        <IconButton
          aria-label={`Send ${lowerItemLabel} to staging`}
          size="small"
          onClick={(event) => {
            if (event.nativeEvent?.pointerType === 'touch') {
              event.stopPropagation();
              onOpenMenu(event.currentTarget, row.original);
              return;
            }
            onSendToStaging(row.original, '', event);
          }}
          onContextMenu={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenMenu(event.currentTarget, row.original);
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
    muiTableBodyCellProps: { align: 'center' },
    muiTableHeadCellProps: {
      align: 'center',
      sx: {
        whiteSpace: 'nowrap',
        '& .Mui-TableHeadCell-Content-Labels': {
          flexWrap: 'nowrap',
        },
        '& .Mui-TableHeadCell-Content-Wrapper': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
    },
  };
  const accountColumn = {
    id: 'accountName',
    header: 'Account Name',
    size: 200,
    accessorFn: (row) => resolveAccountName(row),
  };
  const fieldColumns = visibleFields.map((field) => {
    const normalizedType = normalizeFieldType(field.dataType);
    const isEnumType = normalizedType === 'enum' || normalizedType === 'enumlist';
    const filterConfig = isEnumType ? buildMultiSelectFilter(field.enumValues) : {};
    return {
      id: field.name,
      header: field.displayName || field.name,
      size: 160,
      accessorFn: (row) => getCaseInsensitiveValue(row, field.name),
      ...filterConfig,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        const label = formatChipLabel(value);
        if (!label) return '';
        if (!shouldRenderChip(field.name)) return label;
        const chipColor = resolveChipColor(field.name, label);
        return <Chip label={label} size="small" variant="outlined" color={chipColor} />;
      },
    };
  });
  return [sendColumn, accountColumn, ...fieldColumns];
};
